import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { AuthenticationService } from 'app/auth/service';
import { environment } from 'environments/environment';
import {
  BehaviorSubject,
  of,
  Observable,
  throwError,
  forkJoin,
  combineLatest,
} from 'rxjs';
import {
  switchMap,
  take,
  tap,
  catchError,
  map,
  withLatestFrom,
  mergeMap,
} from 'rxjs/operators';
import { BejovoSzamla, Column, Partner, Penznem } from '../models/torzsek';
import { findByProp } from '../utils';
import { PartnerekService } from './partnerek.service';
import { CikktorzsService } from './cikktorzs.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BizonylatTipusokComponent } from '../pages/bizonylattipusok/bizonylattipusok.component';
import { PenznemekService } from './penznemek.service';

@Injectable({
  providedIn: 'root',
})
export class BejovoKoltsegService {
  private readonly apiEndpoint = 'bejovokoltseg';
  private readonly columnsEndpoint = 'bejovokoltseg/columns';
  private readonly listaEndpoint = 'service/helplib/oldal_lista/bk';
  private readonly updateEndpoint = 'service/helplib/bizonylat_frissites/bk';
  private lastFetch: Date = new Date();
  private bejovokoltseg = JSON.parse(localStorage.getItem('bejovokoltseg'));

  private readonly _data = new BehaviorSubject<any[]>(null);
  private readonly _selectedData = new BehaviorSubject<any>(null);
  public data: any[] = [];
  private pageNum: number;
  public readonly streamEnd = new BehaviorSubject<boolean>(false);
  private raktarId = JSON.parse(localStorage.getItem('currentUser'))?.raktar;

  // private state$ = combineLatest([
  //   this._data,
  //   this.partnerekService.data$,
  //   this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
  //   this.penznemekService.initData$
  // ]).pipe(
  //   switchMap(([state, partnerek, cikkek, penznemek]) => {
  //     return state ? of(this.mapBejovoSzamlak(state, partnerek, cikkek, penznemek)) : this.bejovokoltseg?.db > 0 ? this.getDBData() : this.fetchData()
  //   })
  // );

  private state$ = combineLatest([
    this._data,
    this.partnerekService.data$,
    //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
    this.penznemekService.initData$
  ]).pipe(
    switchMap(([state, partnerek, penznemek]) =>
      state ? of(this.mapBejovoSzamlak(state, partnerek, penznemek)) : this.fetchData()
    )
  );

  public data$ = combineLatest([
    this.state$,
    this.authService.currentUser$,
  ])
  .pipe(map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  );

  constructor(
    private authService: AuthenticationService,
    private partnerekService: PartnerekService,
    private cikkService: CikktorzsService,
    private api: ApiService,
    private stateService: StateService,
    private dbService: NgxIndexedDBService,
    private penznemekService: PenznemekService
  ) {}

  get columns$(): Observable<Column[]> {
    return this.getColumns();
  }

  // get initData$(): Observable<BejovoSzamla[]> {
  //   return this._data.pipe(
  //     take(1),
  //     switchMap((state: BejovoSzamla[]) =>
  //       state
  //         ? forkJoin([
  //           of(state), 
  //           this.partnerekService.initData$, 
  //           this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
  //           this.penznemekService.initData$
  //           ])
  //           .pipe(
  //             map(([kimenoSzamlak, partnerek, cikkek, penznemek]) =>
  //               this.mapBejovoSzamlak(kimenoSzamlak, partnerek, cikkek, penznemek)
  //             )
  //           )
  //         : JSON.parse(localStorage.getItem('bejovokoltseg'))?.db > 0 ? this.getDBData() : this.fetchData()
  //     ),
  //     withLatestFrom(this.authService.currentUser$),
  //     map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  //   );
  // }

  get initData$(): Observable<BejovoSzamla[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: BejovoSzamla[]) =>
        state
          ? forkJoin([
            of(state), 
            this.partnerekService.initData$, 
            //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
            this.penznemekService.initData$])
            .pipe(
              map(([kimenoSzamlak, partnerek, penznemek]) =>
                this.mapBejovoSzamlak(kimenoSzamlak, partnerek, penznemek)
              )
            )
          : this.fetchData()
      ),
      withLatestFrom(this.authService.currentUser$),
      map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
    );
  }

  add(data: BejovoSzamla): Observable<BejovoSzamla> {
    return this.api.post(this.apiEndpoint, this.toJSON(data)).pipe(
      tap((added: BejovoSzamla) => {
        this.stateService.setStateOnAdd(added, this._data);
        // this.dbService.add('bejovokoltseg', added).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<BejovoSzamla> {
    return this.api.delete(this.apiEndpoint, id).pipe(
      tap((storno: BejovoSzamla) => {
        this.stateService.setStateOnUpdate(storno, this._data);
        // this.dbService.update('bejovokoltseg', storno).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  getById(id: string): Observable<BejovoSzamla> {
    return this.data$.pipe(
      take(1),
      map((state) => {
        // Find the data
        const data =
          state.find((item: BejovoSzamla) => item.id === +id) || null;

        // Update the data
        this._selectedData.next(data);

        // Return the data
        return data;
      }),
      switchMap((data) => {
        if (id === 'new') {
          return of(null);
        }

        if (!data) {
          return throwError('Could not found data with id of ' + id + '!');
        }

        return of(data);
      })
    );
  }

  fetchAndMapDataById(id: number | string): Observable<any> {
    return forkJoin([
      this.api.get(`${this.apiEndpoint}/${id}`).pipe(
        catchError((err: any) => {
          console.log(err);
          return of(null);
        })
      ),
      this.partnerekService.initData$,
      //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
      this.penznemekService.initData$,
    ]).pipe(
      map(([bejovoKoltseg, partnerek, penznemek]) => {
        const retVal = this.mapBejovoSzamla(bejovoKoltseg, partnerek, penznemek)
        this.stateService.setStateOnUpdate(retVal, this._data);
        this._selectedData.next(retVal);
        return retVal;
      }
      )
    );
  }

  update(data: BejovoSzamla): Observable<BejovoSzamla> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        tap((updated: BejovoSzamla) => {
          this.stateService.setStateOnUpdate(updated, this._data);
          // this.dbService.update('bejovokoltseg', updated).subscribe();
        }),
        catchError((err: any) => throwError(err))
      );
  }

  getBizonylatValtozas(): any {
    this.lastFetch = new Date();
    const oe = JSON.parse(localStorage.getItem('currentUser')).oe;
    const last = JSON.parse(localStorage.getItem('bejovokoltseg'))?.lastFetch;
    return this.api.postBizonylat(`${this.updateEndpoint}/${oe}/${last}`, "")
           .pipe(
            take(1),
            tap(valtozas => {
              if (this._data.value.length == 0) {
                //this.fetchData().subscribe();
              } else {
                if (valtozas.valtozasok.length > 0) {
                  valtozas.valtozasok.forEach((bizonylat) => {
                    const biz = findByProp(this._data.value, "id", bizonylat.id);
                    if (biz) {
                      this.stateService.setStateOnUpdate(bizonylat, this._data);
                    } else {
                      this.stateService.setStateOnAdd(bizonylat, this._data);
                    }
                  });
                }
                if (valtozas.torolt_lista.length > 0) {
                  valtozas.torolt_lista.forEach((bizonylat) => {
                    const biz = findByProp(
                      this._data.value,
                      "id",
                      bizonylat.modul_id
                    );
                    if (biz) {
                      this.stateService.setStateOnDelete(biz, this._data);
                    }
                  });
                }
              }
    
              localStorage.setItem(
                "bejovokoltseg",
                JSON.stringify({
                  db: this._data.value.length,
                  lastFetch: this.lastFetch,
                })
              );
            }))
    }

  getDBData(): Observable<any> {
    console.log('get bejovokoltsegek from indexedDB')
    // return this.dbService.getAll('nyugta').pipe(
    //   tap((data: any[]) => this._data.next(data.sort((a,b) => b.id - a.id))),
    // );
    return forkJoin([
      this.dbService.getAll('bejovokoltseg').pipe(
       tap((data: any[]) => this._data.next(data.sort((a,b) => b.id - a.id))),
      ),
      this.partnerekService.initData$,
      //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
      this.penznemekService.initData$
    ]).pipe(
      map(([kimenoSzamlak, partnerek, penznemek]) => {
        return this.mapBejovoSzamlak(kimenoSzamlak, partnerek, penznemek)
      })
    );
  }

  private fetchDataById(id: number): Observable<any> {
    return this.api.get(`${this.apiEndpoint}/${id}`).pipe(
      catchError((err: any) => {
        console.log(err);
        return of(null);
      })
    );
  }

  updateBejovoSzamlaRow(id: number, helyesbitoId?: number): Observable<any> {
    return this.fetchDataById(id).pipe(
      tap((bejovoSzamla) => {
        const mappedRow = {
          ...bejovoSzamla,
          fajl: bejovoSzamla.fajlok?.length > 0 ? true : false
        }
        this.stateService.setStateOnUpdate(mappedRow, this._data);
      })
    );
  }

  fetchData(): Observable<BejovoSzamla[]> {
    this.lastFetch = new Date();
    return forkJoin([
      this.api.get(`${this.apiEndpoint}`).pipe(
        // bufferCount(25),
        // concatMap(objs => of(objs)),
        tap((data: any[]) => {
          this._data.next(data);
        }),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.partnerekService.initData$,
      //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
      this.penznemekService.initData$,
    ]).pipe(
      map(([bejovoKoltsegek, partnerek, penznemek]) => {
        localStorage.setItem('bejovokoltseg', JSON.stringify({db: bejovoKoltsegek.length, lastFetch: this.lastFetch}));
        return this.mapBejovoSzamlak(bejovoKoltsegek, partnerek, penznemek)
      })
    );
  }

  // private getColumns(): Observable<Column[]> {
  //   return this.api.get(this.columnsEndpoint).pipe(
  //     map((columns: Column[]) => [
  //       ...columns,
  //       { name: 'tetelek', nullable: true },
  //     ]),
  //     catchError((err: any) => {
  //       console.log(err);

  //       return of([]);
  //     })
  //   );
  // }

  private getColumns(): Observable<Column[]> {
      return this.api.get(this.columnsEndpoint).pipe(
        map((columns: Column[]) => {
        const mappedColumns = [
          ...columns,
          { name: 'tetelek', nullable: true },
        ]
        localStorage.setItem('bejovo-koltseg_columns', JSON.stringify(mappedColumns));
        return mappedColumns;
        }),
        catchError((err: any) => {
          console.log(err);
  
          return of([]);
        })
      );
  }

  private mapBejovoSzamlak(
    bejovoSzamlak: BejovoSzamla[],
    partnerek: Partner[],
    //cikkek: any[],
    penznemek: any[]
  ): BejovoSzamla[] {
    return bejovoSzamlak.map((e: any) => {
      let sztornozhato = true;
      const penznemObj: Penznem = findByProp(
        penznemek,
        'id',
        e.penznem_id
      );
  
      const penznem = penznemObj?.outfix || penznemObj?.prefix;

      // e.tetelek.forEach(tetel => {
      //   if(tetel.bevetelezes_id) {
      //     return
      //   }
      //   if(cikkek?.find(item => tetel.cikk_id == item.id)?.aktualis_mennyiseg <= tetel.mennyiseg) {
      //     sztornozhato = false;
      //   }
      // })
      return {
        ...e,
        partner: findByProp(partnerek, 'id', e.partner_id)?.nev,
        szamla_kelte: new Date(e.szamla_kelte),
        teljesites_datum: new Date(e.teljesites_datum),
        fizetesi_hatarido: new Date(e.fizetesi_hatarido),
        sztornozhato: sztornozhato,
        penznem
      }
    });
  }

  private mapBejovoSzamla(
    bejovoSzamla: any,
    partnerek: Partner[],
    //cikkek: any[],
    penznemek: any[]
  ): BejovoSzamla[] {
      let sztornozhato = true;
      const penznemObj: Penznem = findByProp(
        penznemek,
        'id',
        bejovoSzamla.penznem_id
      );
  
      const penznem = penznemObj?.outfix || penznemObj?.prefix;

      // bejovoSzamla.tetelek.forEach(tetel => {
      //   if(tetel.bevetelezes_id) {
      //     return
      //   }
      //   if(cikkek?.find(item => tetel.cikk_id == item.id)?.aktualis_mennyiseg <= tetel.mennyiseg) {
      //     sztornozhato = false;
      //   }
      // })
      return {
        ...bejovoSzamla,
        partner: findByProp(partnerek, 'id', bejovoSzamla.partner_id)?.nev,
        szamla_kelte: new Date(bejovoSzamla.szamla_kelte),
        teljesites_datum: new Date(bejovoSzamla.teljesites_datum),
        fizetesi_hatarido: new Date(bejovoSzamla.fizetesi_hatarido),
        sztornozhato: sztornozhato,
        penznem
      }
  }

  private toJSON(data: any): BejovoSzamla {
    return {
      ...data,
      teljesites_datum: new Date(new Date(data.teljesites_datum).setHours(new Date(data.teljesites_datum).getHours() + 3)),
      fizetesi_hatarido: new Date(new Date(data.fizetesi_hatarido).setHours(new Date(data.fizetesi_hatarido).getHours() + 3)),
      cim: JSON.stringify(data.cim),
    };
  }

  generatePdf(payload: { id: number; sztorno?: boolean }): Observable<any> {
    return this.api
      .postBizonylat(`${environment.pdfGeneralas}/${this.apiEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  sztorno(id: number): Observable<any> {
    return this.api.delete(this.apiEndpoint, id, 'sztornózás').pipe(
      tap((storno: any) => {
        this.stateService.setStateOnUpdate(storno, this._data);
        // this.dbService.update('bejovokoltseg', storno).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  updateState(data: any[]): void{
    data.forEach(item => {
      this.stateService.setStateOnUpdate(item, this._data);
    })
  }

  getMennyiseg(): any {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));

    return this.api.postBizonylat(`service/helplib/ossz_bizonylat/bk/${currentUser.id}`, "");
  }

  clearData(): any {
    this.data = [];
  }
}
